import React from 'react';
import { BallTriangle } from 'react-loader-spinner';

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'Selected':
    case 'Accepted':
    case 'Going To Attend':
    case 'Will Attend':
      return 'bg-green-100 text-green-800';
    case 'Not Selected':
    case 'Declined':
    case 'Not Going To Attend':
    case 'Cannot Attend':
      return 'bg-red-100 text-red-800';
    case 'On Hold':
      return 'bg-yellow-100 text-yellow-800';
    case 'Pending':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const PlacementDetails = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex h-56 w-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <BallTriangle color="#5431ed" />
          <p className="text-sm text-gray-500">Loading placement data...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Failed to load placement data. Check server connection.
          </p>
        </div>
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No interviews yet.
          </p>
        </div>
      </div>
    );
  }

  const { interviewMetrics, offerStatus, interviews } = data;

  const getInterviewResponse = (interview) => {
    let responseText = '';
    let responseStatus = '';

    if (interview.status && interview.status !== 'Pending') {
      responseText =
        interview.status === 'Accepted'
          ? 'Offer Accepted'
          : interview.status === 'Declined'
          ? 'Offer Declined'
          : interview.status;
      responseStatus = interview.status;
    } else {
      const rounds = interview.rounds || [];
      const latestRound =
        rounds.length > 0 ? rounds[rounds.length - 1] : null;
      if (
        latestRound &&
        latestRound.studentStatus &&
        latestRound.studentStatus !== 'Pending'
      ) {
        responseText =
          latestRound.studentStatus === 'Going To Attend'
            ? 'Will Attend'
            : latestRound.studentStatus === 'Not Going To Attend'
            ? 'Cannot Attend'
            : latestRound.studentStatus;
        responseStatus = latestRound.studentStatus;
      }
    }

    return { responseText, responseStatus };
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
        <h5 className="mb-2 text-sm font-medium dark:text-white">Overview</h5>
        <div className="space-y-2 text-sm dark:text-white">
          <p className="flex justify-between">
            <span>Total Interviews:</span>
            <span>{interviewMetrics?.totalInterviews ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Attended:</span>
            <span>{interviewMetrics?.attended ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Selected:</span>
            <span>{interviewMetrics?.selected ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Rejected:</span>
            <span>{interviewMetrics?.rejected ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Upcoming:</span>
            <span>{interviewMetrics?.upcoming ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Offers:</span>
            <span>{offerStatus?.totalOffers ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Accepted:</span>
            <span>{offerStatus?.acceptedOffers ?? 0}</span>
          </p>
          <p className="flex justify-between">
            <span>Pending Offers:</span>
            <span>{offerStatus?.pendingOffers ?? 0}</span>
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
        <h5 className="mb-2 text-sm font-medium dark:text-white">
          Recent Interviews
        </h5>
        {interviews?.length > 0 ? (
          <div className="space-y-2">
            {interviews.map((interview, index) => {
              const { responseText, responseStatus } =
                getInterviewResponse(interview);
              return (
                <div
                  key={index}
                  className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0 last:pb-0 dark:border-gray-800"
                >
                  <div className="flex flex-col">
                    <span className="font-medium dark:text-white">
                      {interview.company || 'N/A'}
                    </span>
                    <span className="text-xs text-gray-700 dark:text-gray-400">
                      {interview.jobTitle || 'N/A'}
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${getStatusBadgeColor(
                        interview.overallStatus
                      )}`}
                    >
                      HR: {interview.overallStatus || 'Pending'}
                    </span>
                    {responseText && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-sm ${getStatusBadgeColor(
                          responseStatus
                        )}`}
                      >
                        Student: {responseText}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No interviews yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default PlacementDetails;
